import {
    ChangeEvent,
    FC,
    ReactElement,
    useState,
} from 'react'
import { useNavigate } from 'react-router'

import {
    Button,
    Card,
    Checkbox,    InfoListBlock,
    InfoListItem,
    LoadingSpinner } from '@/components'
import { DateInput } from '@/compositions'
import { transformDateAndTimesToPeriod } from '@/entities/Period/PeriodTransformers'
import { formatDate } from '@/helpers/date'
import { trans } from '@/helpers/trans'
import { createPlanningPreference } from '@/redux/preferences/preferenceActions'
import { useTypedDispatch, useTypedSelector } from '@/redux/store'
import { DateFormat } from '@/types'

import './CreatePlanningPreferencePage.scss'

// A whole day runs from 04:00 until 04:00 the next day
const wholeDayTime = '04:00'

export const CreatePlanningPreferencePage: FC = (): ReactElement => {
    const dispatch = useTypedDispatch()
    const navigate = useNavigate()
    const { loading } = useTypedSelector(state => state.preferenceReducer)
    const [selectedStartDate, setSelectedStartDate] = useState(() => new Date())
    const [isWholeDay, setIsWholeDay] = useState(false)
    const [startTime, setStartTime] = useState('08:00')
    const [endTime, setEndTime] = useState('18:00')
    const [reason, setReason] = useState('')

    const handleWholeDayChange = (event: ChangeEvent<HTMLInputElement>): void => {
        setIsWholeDay(event.target.checked)
    }

    const handleStartTimeChange = (event: ChangeEvent<HTMLInputElement>): void => {
        setStartTime(event.target.value)
    }

    const handleEndTimeChange = (event: ChangeEvent<HTMLInputElement>): void => {
        setEndTime(event.target.value)
    }

    const handleReasonChange = (event: ChangeEvent<HTMLTextAreaElement>): void => {
        setReason(event.currentTarget.value)
    }

    const handleSubmitClick = (): void => {
        const period = transformDateAndTimesToPeriod(
            selectedStartDate,
            isWholeDay ? wholeDayTime : startTime,
            isWholeDay ? wholeDayTime : endTime,
        )
        const startDate = formatDate(period.start, DateFormat.apiDateTime)
        const endDate = formatDate(period.end, DateFormat.apiDateTime)

        dispatch(createPlanningPreference(startDate, endDate, reason, navigate))
    }

    return (
        <>
            <Card className="create-planning-preference__header">
                {trans('pages.planningPreferences.createPlanningPreference')}
            </Card>
            <ul>
                <InfoListItem label={trans('common.date')}>
                    <DateInput
                        hideLabel
                        label={trans('common.date')}
                        onChange={setSelectedStartDate}
                        value={selectedStartDate}
                    />
                </InfoListItem>
                <InfoListItem label={trans('common.isWholeDay')}>
                    <Checkbox
                        label={trans('common.isWholeDay')}
                        hideLabel
                        name="isWholeDay"
                        isChecked={isWholeDay}
                        onChange={handleWholeDayChange}
                    />
                </InfoListItem>
                <InfoListItem disabled={isWholeDay} label={trans('common.startTime')}>
                    <div className="form-group">
                        <input
                            className="form-control"
                            type="time"
                            value={isWholeDay ? wholeDayTime : startTime}
                            disabled={isWholeDay}
                            onChange={handleStartTimeChange}
                        />
                    </div>
                </InfoListItem>
                <InfoListItem disabled={isWholeDay} label={trans('common.endTime')}>
                    <div className="form-group">
                        <input
                            className="form-control"
                            type="time"
                            value={isWholeDay ? wholeDayTime : endTime}
                            disabled={isWholeDay}
                            onChange={handleEndTimeChange}
                        />
                    </div>
                </InfoListItem>
                <InfoListBlock label={trans('common.reason')}>
                    <textarea
                        className="form-control border-0"
                        onChange={handleReasonChange}
                        placeholder={trans('common.description')}
                        maxLength={125}
                    />
                </InfoListBlock>
            </ul>
            <Button
                text={trans('common.save')}
                disabled={! reason}
                onClick={handleSubmitClick}
                className="create-planning-preference__submit-button"
            />
            {loading && <LoadingSpinner />}
        </>
    )
}
